/* eslint-disable react/no-danger */
import React, { Component } from 'react';
import dynamic from 'next/dynamic';
import { animateScroll as scroll } from 'react-scroll';
import sanitizeHtml from 'sanitize-html';
import styled from 'styled-components';
import axios from 'axios';
import { WordPressAPI } from '../../config';
import { Jumbotron } from '@/components/styles/JumbotronStyles';
import PayrollHero from '@/components/styles/PayrollHeroStyles';
import FooterComponent from '@/components/Footer/FooterComponent';
import PayrollContentComponent from '@/components/PayrollContentComponent/PayrollContentComponent';
import { formatPhone } from '../../services/formatPhone';
import Header from '@/components/Header/Header';
import SEOHead from '@/components/SEOHead/SEOHead';
import validatePaidCampaign from '@helpers/validatePaidCampaign';
import parse from 'html-react-parser';

const TrustPilotComponent = dynamic(
  () => import('@/components/TrustPilot/TrustPilotComponent'),
  { ssr: false }
);

const PullUpScheduler = dynamic(() =>
  import('@/components/PullUpScheduler/PullUpSchedulerV2')
);

const FooterNewsModal = dynamic(import('@/components/FooterNewsModal'));

const TPStyles = styled.div`
padding: 20px 0;
position: relative;

.tp-container {
  margin: 0 auto;
  max-width: 100%;
  
  ${props => props.theme.media.md} {
    max-width: 67%;
  }
}
`;

const sanitizeSettings = {
  allowedTags: ['p', 'strong'],
};

class Payroll extends Component {
  state = {
    PaidCampaign: 1,
    hideSchedulerHeader: false,
  };

  componentDidUpdate() {
    if (this.state.isSchedulerOpen) {
      document.body.classList.add('no-overflow');
    } else {
      document.body.classList.remove('no-overflow');
    }
  }

  scrollUp = () => {
    scroll.scrollToTop({
      duration: 250,
    });
  };

  toggleIsSchedulerOpen = () => {
    this.setState(prevState => ({
      isSchedulerOpen: !prevState.isSchedulerOpen,
      hideSchedulerHeader: !prevState.isSchedulerOpen,
    }));
  };

  render() {
    const { data, blogModuleData } = this.props;
    const { title, acf } = this.props.data;
    const { conditionalPhone, officeHoursOpen } = this.state;
    const paid_campaign = validatePaidCampaign(acf.paid_campaign);
    const additional_questions = acf.additional_questions;

    // Use custom title tag if it's filled out in WP, otherwise use the page name.
    let pageTitle
    acf?.page_title_tag
      ? pageTitle = acf.page_title_tag
      : pageTitle = title.rendered

    return (
      <div>
        <Header
          AdditionalQuestions={additional_questions}
          PaidCampaign={typeof (paid_campaign) === 'undefined' ? 1 : paid_campaign}
          phone_number={acf.phone_number_setup}
          leadValues={acf.lead_values}
          hideSchedulerHeader={this.state.hideSchedulerHeader}
        />
        <Jumbotron background={acf.hero_background_image.url} className="payroll-jumbotron">
          <SEOHead>
            <title>{pageTitle}</title>
            <meta
              name="description"
              content={acf?.meta_description || ''}
              key="description"
            />
            <script
              type="text/javascript"
              src="//app-ab33.marketo.com/js/forms2/js/forms2.min.js"
            />

            {parse(`<script type="application/ld+json">
              {
                "@context": "https://schema.org",
                "@type": "FAQPage",
                "mainEntity": [{
                  "@type": "Question",
                "name": "How do I pay myself as a business owner?",
                "acceptedAnswer": {
                  "@type": "Answer",
                "text": "One of the most important decisions you’ll make as an owner is how and how much you’ll pay yourself. The law orders that you follow certain guidelines when it comes to compensation. There are two standard compensation methods that business owners can consider: an owner’s draw and a salary."
                  }
                },{
                              "@type": "Question",
                            "name": "How do I handle payroll taxes for employees working out of state?",
                            "acceptedAnswer": {
                              "@type": "Answer",
                            "text": "Employees who work out of state must submit a withholding exemption or non-residency certificate to their state's tax agency. This exempts them from tax withholding in the state where your business is headquartered. We can handle that on their behalf when you use our payroll service."
                  }
                }]
              }
            </script>`)}

          </SEOHead>
          <PayrollHero>
            <div className="large-image-container">
              <img className="large-hero-image" src={acf.common_content.get_started.image} alt="payroll image" width="500px" height="500px" />
            </div>
            <div className="container">
              <h1 className="mb-2">{acf.hero_title}</h1>
              <div
                className="sub-header"
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(acf.hero_sub_title, sanitizeSettings),
                }}
              />
              <div className="sub-text">
                Get started with a free consultation
              </div>
              <div className="row justify-content-md-center">
                <div className="col-sm-12 col-md-10 col-lg-8 col-xl-6">
                  <div className="row justify-content-md-center">
                    <div className="col-md-8">
                      <div className="small-image-container">
                        <img className="small-hero-image" src={acf.common_content.get_started.image} alt="free consultation" width="500px" height="500px" />
                      </div>
                      <button
                        type="button"
                        className="btn btn-primary btn-block"
                        onClick={this.toggleIsSchedulerOpen}
                        data-cy="btn-lightbox-start"
                      >
                        Schedule My Free Call
                      </button>
                      {officeHoursOpen && (
                        <a
                          href={`tel:${acf.phone_number_setup?.online_phone_number}`}
                          className="btn btn-clear btn-block d-sm-none"
                        >
                          {formatPhone(acf.phone_number_setup?.online_phone_number)}
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </PayrollHero>
        </Jumbotron>

        <TPStyles>
          <div className="tp-container">
            <TrustPilotComponent
              templateID="5406e65db0d04a09e042d5fc"
              height="28px"
              alt
            />
          </div>
        </TPStyles>

        <PayrollContentComponent
          buttonAction={this.toggleIsSchedulerOpen}
          data={data}
          acf={data.acf}
          blogModuleData={blogModuleData}
        />

        {/* Email Newsletter */}
        <FooterNewsModal />
        <FooterComponent
          phone_number={acf.phone_number_setup}
        />

        {this.state.isSchedulerOpen && (
          <PullUpScheduler
            setIsSchedulerOpen={this.toggleIsSchedulerOpen}
            leadValues={acf.lead_values}
            PaidCampaign={this.state.PaidCampaign}
            AdditionalQuestions={additional_questions}
          />
        )}
      </div>
    );
  }
}

export const getStaticProps = async () => {
  const GET_POST_URL = `${process.env.NEXT_PUBLIC_BLOG}/wp-json/wp/v2/posts`;

  const axiosWP = axios.get(`${WordPressAPI.pages}/17070`);
  const axiosBlog1 = axios.get(`${GET_POST_URL}?slug=small-business-payroll&_embed=true`);
  const axiosBlog2 = axios.get(`${GET_POST_URL}?slug=make-payroll-more-efficient&_embed=true`);
  const axiosBlog3 = axios.get(`${GET_POST_URL}?slug=payroll-tax-return&_embed=true`);

  const [{ data }, blogData1, blogData2, blogData3] = await Promise.all([axiosWP, axiosBlog1, axiosBlog2, axiosBlog3]);


  const post1 = blogData1.data[0];
  const post2 = blogData2.data[0];
  const post3 = blogData3.data[0];


  const blogModuleData = [post1, post2, post3];

  return {
    props: {
      data,
      blogModuleData
    },
    // revalidate: 1,
  };
};

export default Payroll;
